import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Heading,
    Text,
    Input,
    Grid,
    FormControl,
    FormLabel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuGroup,
    Select,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow,
    Badge,
    Spinner,
    Center,
    useToast,
    VStack,
    Tag,
} from '@chakra-ui/react';
import {
    FiArrowLeft,
    FiFilter,
    FiPlus,
    FiTrash2,
    FiTarget,
    FiX
} from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import Header from '../../../components/Header';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { PRIMARY_BG, PRIMARY_BORDER, PRIMARY_TINT } from '../../../theme/brand';
const FunnelBuilder = ({ isEdit = false, readOnly = false }) => {
    const { id: funnelId } = useParams();
    const toast = useToast();
    const [funnelName, setFunnelName] = useState('');
    const [funnelDescription, setFunnelDescription] = useState('');
    // PERM-093: the funnel's current status, re-hydrated in fetchFunnelDetails
    // and sent back as-is on update, instead of always forcing 'active'.
    const [funnelStatus, setFunnelStatus] = useState('active');
    const [builderSteps, setBuilderSteps] = useState([]);
    const [selectedBreakdown, setSelectedBreakdown] = useState(null);
    const [selectedBreakdown2, setSelectedBreakdown2] = useState(null);
    const [windowUnit, setWindowUnit] = useState('days');
    const [windowValue, setWindowValue] = useState(7);
    const [availableEvents, setAvailableEvents] = useState([]);
    const [funnelData, setFunnelData] = useState(null);
    const [eventSearch, setEventSearch] = useState('');
    const [customRange, setCustomRange] = useState(false);
    const [measurement, setMeasurement] = useState('conversion_rate');
    const [conversionMap, setConversionMap] = useState({});
    const [stepAttributes, setStepAttributes] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [hasLoadedInitialAnalytics, setHasLoadedInitialAnalytics] = useState(false);
    const [save, setsaving] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    // Backend groups funnel permissions under the list page's own feature slug,
    // not this builder's route (which includes the funnel id when editing/viewing).
    const permParams = "analytics/funnels";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Same component renders /funnels-create, /funnels-edit/:id (via EditFunnel) and
    // /funnels-view/:id — the permission that actually governs saving depends on which
    // one this mount is. `funnelId` (from the route param, already read above) tells us
    // create vs edit without needing a separate isEdit-keyed slug.
    const requiredSlug = readOnly ? "funnel-view" : (funnelId ? "funnel-edit" : "funnel-create");
    const canSave = hasPermission(requiredSlug, permissions);
    const canEditFunnel = hasPermission("funnel-edit", permissions);
    // The canvas always renders now; only the mutating controls are gated. When the
    // route is already read-only by design (/funnels-view/:id), keep it read-only
    // regardless of permission — permission only governs whether the "Edit" button
    // (which requires funnel-edit) is offered from there.
    const effectiveReadOnly = readOnly || !canSave;
    const cardBg = '#FFFFFF';
    const borderColor = '#E2E8F0';

    // Breakdown dimensions are the clean, low-cardinality event properties for this
    // data (banking fields), captured at the contact's first funnel event that carries
    // them. High-cardinality noise (account numbers, names, reason, amounts) is excluded.
    const breakdownOptions = [
        { value: 'currency', label: 'Currency', icon: FiTarget },
        { value: 'rail', label: 'Rail', icon: FiTarget },
        { value: 'card_type', label: 'Card Type', icon: FiTarget },
        { value: 'source', label: 'Source', icon: FiTarget },
        { value: 'channel', label: 'Channel', icon: FiTarget },
        { value: 'country', label: 'Country', icon: FiTarget },
    ];

    useEffect(() => {
        fetchEvents();

        if (funnelId) {
            fetchFunnelDetails(funnelId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [funnelId]);

    // Conversion Events config drives the "Value" measure (which property to sum on
    // the final/conversion step). Optional — Value falls back to a plain count if absent.
    useEffect(() => {
        (async () => {
            try {
                const r = await API.get(`${process.env.REACT_APP_API_URL}/event-conversations?limit=200`, { headers: getApiHeaders() });
                const map = {};
                (r.data?.data?.event_conversations || []).forEach((c) => { if (!map[c.event_name]) map[c.event_name] = c; });
                setConversionMap(map);
            } catch (e) { /* non-fatal: Value measure degrades to count */ }
        })();
    }, []);

    useEffect(() => {
        if (builderSteps.length > 0) {
            const timeoutId = setTimeout(() => {
                fetchFunnelData();
            }, 300);

            return () => clearTimeout(timeoutId);
        } else {
            setFunnelData(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [builderSteps, selectedBreakdown, selectedBreakdown2, windowUnit, windowValue, measurement]);

    const fetchEvents = async () => {
        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
                {},
                { headers: getApiHeaders() }
            );



            if (response.data.status && response.data.data && response.data.data.events) {
                setAvailableEvents(response.data.data.events);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };

    // FN09 — a saved funnel persists only the raw event KEY per step, so on reload
    // the step labels render the snake_case key (e.g. "transfer_sent"). Once the
    // event catalogue (the same list the picker uses) is loaded, resolve each step's
    // key to its friendly display name. Every label surface — Metric card, chart
    // x-axis, and breakdown table headers — reads builderStep.name, so upgrading it
    // here fixes all of them at once. Events missing from the catalogue keep their
    // raw key (the render sites already fall back to it), never blank/"undefined".
    useEffect(() => {
        if (!availableEvents.length || !builderSteps.length) return;
        const nameByKey = new Map(availableEvents.map((e) => [e.name, e.display_name]));
        setBuilderSteps((prev) => {
            let changed = false;
            const next = prev.map((s) => {
                const disp = nameByKey.get(s.eventName);
                if (disp && disp !== s.name) { changed = true; return { ...s, name: disp }; }
                return s;
            });
            return changed ? next : prev; // same ref when nothing changes → no loop
        });
    }, [availableEvents, builderSteps]);

    const fetchFunnelDetails = async (id) => {
        setLoadingDetails(true);
        try {
            const response = await API.get(
                `${process.env.REACT_APP_API_URL}/funnels/${id}`,
                { headers: getApiHeaders() }
            );



            if (response.data.status && response.data.data) {
                const funnelDetails = response.data.data.funnel || response.data.data;

                setFunnelName(funnelDetails.name || '');
                setFunnelDescription(funnelDetails.description || '');
                setFunnelStatus(funnelDetails.status || 'active');
                setWindowUnit(funnelDetails.default_window_unit || 'days');
                setWindowValue(funnelDetails.default_window_value || 7);
                // FN11: reopen with the persisted Measure (Value (sum) funnel
                // reopens as value, not Conversion Rate). Same pattern as window/
                // breakdown above. The "Summing … " hint + headline re-derive
                // from measurement='value' + the step's conversion config.
                setMeasurement(funnelDetails.default_measure || 'conversion_rate');

                if (funnelDetails.steps && Array.isArray(funnelDetails.steps)) {
                    const mappedSteps = funnelDetails.steps.map((step, index) => ({
                        id: step._id || step.id || `step_${Date.now()}_${index}`,
                        name: step.event || step.name || 'Unknown Step',
                        eventName: step.event || step.eventName || step.name,
                        order: step.order !== undefined ? step.order : index,
                        // FN05: re-hydrate saved per-step property filters so the filter
                        // panel + filtered counts reflect what was persisted.
                        filters: Array.isArray(step.filters) ? step.filters : []
                    }));
                    setBuilderSteps(mappedSteps);
                }

                if (funnelDetails.default_breakdown) {
                    setSelectedBreakdown(funnelDetails.default_breakdown);
                }
                // FN14: re-hydrate the 2nd breakdown so a composite funnel reopens
                // with BOTH dimensions (the useEffect re-fetch then includes both).
                if (funnelDetails.default_breakdown2 && funnelDetails.default_breakdown2 !== 'none') {
                    setSelectedBreakdown2(funnelDetails.default_breakdown2);
                }

                if (funnelDetails.steps && funnelDetails.steps.length > 0) {
                    // FN14: request the COMPOSITE breakdown on first paint (both
                    // dims, comma-joined) so the initial GET /analytics returns the
                    // compound rows — identical to the interactive /preview path.
                    // Previously only the 1st dim was sent, so a saved two-breakdown
                    // funnel rendered rail-only until the user touched a control,
                    // while the title still claimed "Rail × Currency".
                    const initialBreakdown = [funnelDetails.default_breakdown, funnelDetails.default_breakdown2]
                        .filter((b) => b && b !== 'none')
                        .join(',');
                    fetchInitialAnalytics(
                        id,
                        initialBreakdown,
                        funnelDetails.default_window_unit || 'days',
                        funnelDetails.default_window_value || 7
                    );
                }
            }
        } catch (error) {
            console.error('Error fetching funnel details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const fetchInitialAnalytics = async (id, breakdown, unit, value) => {
        if (hasLoadedInitialAnalytics) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                window_unit: unit,
                window_value: value.toString(),
            });

            if (breakdown) {
                params.append('breakdown', breakdown);
            }

            const response = await API.get(
                `${process.env.REACT_APP_API_URL}/funnels/${id}/analytics?${params}`,
                { headers: getApiHeaders() }
            );



            if (response.data.status && response.data.data) {
                if (breakdown) {
                    // FN14: `breakdown` may be a composite ("rail,currency"); key
                    // funnelData by the PRIMARY dim (the render reads
                    // funnelData[selectedBreakdown]), while the value carries the
                    // compound breakdown_rows the composite request returned.
                    const key = String(breakdown).split(',')[0];
                    setFunnelData({ [key]: response.data.data });
                } else {
                    setFunnelData({ default: response.data.data });
                }
            }

            setHasLoadedInitialAnalytics(true);
        } catch (error) {
            console.error('Error fetching initial analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFunnelData = async () => {
        if (builderSteps.length === 0 || loading) return;

        setLoading(true);
        try {
            const payload = {
                steps: builderSteps.map(step => {
                    const valid = (step.filters || []).filter(f => {
                        if (!f.property) return false;
                        if (f.operator === 'is_set' || f.operator === 'is_not_set') return true;
                        if (f.value === '') return false;
                        // numeric operators need a numeric value (backend rejects otherwise)
                        if (['gt', 'lt', 'gte', 'lte'].includes(f.operator) && !Number.isFinite(Number(f.value))) return false;
                        return true;
                    });
                    return valid.length ? { event: step.eventName, filters: valid } : step.eventName;
                }),
                window_unit: windowUnit,
                window_value: windowValue,
            };

            if (selectedBreakdown) {
                payload.breakdown = selectedBreakdown2 ? [selectedBreakdown, selectedBreakdown2] : selectedBreakdown;
            }

            // FN08 — Value measure. Resolve the value source from the FIRST step (in
            // funnel order) whose event has an "Event Property" value source configured
            // in Tools → Conversion Events, regardless of step count. Previously only the
            // FINAL step's config was read, so a 2+ step funnel whose last step isn't a
            // configured conversion event silently dropped value_property and the backend
            // had nothing to aggregate. (Single-step funnels resolve to that one step.)
            if (measurement === 'value') {
                for (const s of builderSteps) {
                    const vc = conversionMap[s.eventName]?.value_config;
                    if (vc?.source_type === 'from_property' && vc.value) {
                        payload.value_property = vc.value;
                        // Send value_event too: the Event service otherwise sums the
                        // property on the LAST step, but the value source may live on an
                        // earlier step (e.g. trans_amount on transfer_sent, step 1). Sum
                        // it on the step that actually owns the property.
                        payload.value_event = s.eventName;
                        break;
                    }
                }
            }

            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/funnels/preview`,
                payload,
                { headers: getApiHeaders() }
            );



            if (response.data.status && response.data.data) {
                if (selectedBreakdown) {
                    setFunnelData({ [selectedBreakdown]: response.data.data });
                } else {
                    setFunnelData({ default: response.data.data });
                }
            }
        } catch (error) {
            console.error('Error fetching funnel data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddStep = (event) => {
        const isDuplicate = builderSteps.some(step => step.eventName === event.name);

        if (isDuplicate) {
            toast({
                title: 'Metric already added',
                description: `"${event.display_name}" is already in the funnel steps.`,
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
            return;
        }

        const newStep = {
            id: `step_${Date.now()}`,
            name: event.display_name,
            eventName: event.name,
            order: builderSteps.length,
            filters: []
        };
        setBuilderSteps([...builderSteps, newStep]);
    };

    const handleRemoveStep = (stepId) => {
        setBuilderSteps(builderSteps.filter(step => step.id !== stepId));
    };

    // ── Per-step property filters (Mixpanel-style: "amount > 5000") ──────────────
    const fetchStepAttributes = async (eventName) => {
        if (!eventName || stepAttributes[eventName] !== undefined) return;
        try {
            const r = await API.post(`${process.env.REACT_APP_API_URL}/fetch-unique-attributes`, { event: eventName }, { headers: getApiHeaders() });
            setStepAttributes(p => ({ ...p, [eventName]: r.data?.data?.attributes || [] }));
        } catch { setStepAttributes(p => ({ ...p, [eventName]: [] })); }
    };
    const addStepFilter = (stepId) => {
        setBuilderSteps(steps => steps.map(s => s.id === stepId
            ? { ...s, filters: [...(s.filters || []), { property: '', operator: 'eq', value: '' }] } : s));
    };
    const updateStepFilter = (stepId, fi, patch) => {
        setBuilderSteps(steps => steps.map(s => s.id === stepId
            ? { ...s, filters: (s.filters || []).map((f, i) => i === fi ? { ...f, ...patch } : f) } : s));
    };
    const removeStepFilter = (stepId, fi) => {
        setBuilderSteps(steps => steps.map(s => s.id === stepId
            ? { ...s, filters: (s.filters || []).filter((_, i) => i !== fi) } : s));
    };

    const handleSelectBreakdown = (breakdown) => {
        // First pick = primary dimension; second pick = composite (max 2).
        if (!selectedBreakdown) { setSelectedBreakdown(breakdown); }
        else if (breakdown !== selectedBreakdown) { setSelectedBreakdown2(breakdown); }
    };
    const onBack = (breakdown) => {
        navigate(-1)
    };

    const handleRemoveBreakdown = () => {
        // Removing the primary promotes the secondary so the chart keeps a dimension.
        setSelectedBreakdown(selectedBreakdown2 || null);
        setSelectedBreakdown2(null);
    };
    const handleRemoveBreakdown2 = () => {
        setSelectedBreakdown2(null);
    };

    // FN05: build the steps payload for save/update. Each step carries its
    // per-step property filters (same shape the preview request uses), so saved
    // funnels re-apply their filters on reopen. Invalid/empty filters are dropped
    // with the identical rule the preview uses.
    const buildStepsPayload = () => builderSteps.map((step, index) => {
        const filters = (step.filters || []).filter(f => {
            if (!f.property) return false;
            if (f.operator === 'is_set' || f.operator === 'is_not_set') return true;
            if (f.value === '') return false;
            if (['gt', 'lt', 'gte', 'lte'].includes(f.operator) && !Number.isFinite(Number(f.value))) return false;
            return true;
        });
        return { event: step.eventName, order: index, filters };
    });

    const handleSaveFunnel = async (statusArg) => {
        // BUG-FN_001: a funnel can be created directly as a draft. `statusArg` is
        // 'draft' from the Save as Draft button or 'active' from Save Funnel; guard
        // against an onClick event object being passed as the status.
        const status = statusArg === 'draft' ? 'draft' : 'active';
        setsaving(true)
        try {
            const payload = {
                name: funnelName,
                description: funnelDescription,
                status,
                steps: buildStepsPayload(),
                default_window_unit: windowUnit,
                default_window_value: windowValue,
                default_breakdown: selectedBreakdown,
                default_breakdown2: selectedBreakdown2 || null, // FN14: persist the 2nd breakdown
                default_measure: measurement // FN11: persist the Measure with the funnel
            };

            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/funnels/create`,
                payload,
                { headers: getApiHeaders() }
            );

            if (response.data.status) {
                toast({
                    title: status === 'draft' ? 'Funnel saved as draft' : 'Funnel created and activated',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
                setsaving(false)
                // FN13: go to the list and signal it to refetch, so the new funnel
                // + Total Funnels count appear immediately. navigate(-1) alone did
                // not reliably refetch the list (it read pre-creation data).
                navigate('/analytics/funnels', { state: { funnelSaved: Date.now() } });
            }
        } catch (error) {
            console.error('Error saving funnel:', error);
            toast({
                title: 'Error saving funnel',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setsaving(false)
        }
    };

    const handleUpdateFunnel = async () => {
        try {
            const payload = {
                name: funnelName,
                description: funnelDescription,
                status: funnelStatus,
                steps: buildStepsPayload(),
                default_window_unit: windowUnit,
                default_window_value: windowValue,
                default_breakdown: selectedBreakdown,
                default_breakdown2: selectedBreakdown2 || null, // FN14: persist the 2nd breakdown
                default_measure: measurement // FN11: persist the Measure with the funnel
            };

            const response = await API.put(
                `${process.env.REACT_APP_API_URL}/funnels/${funnelId}`,
                payload,
                {
                    headers: {
                        ...getApiHeaders(),
                        'content-type': 'application/json'
                    }
                }
            );

            if (response.data.status) {
                toast({
                    title: 'Funnel updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
                onBack && onBack();
            }
        } catch (error) {
            console.error('Error updating funnel:', error);
            toast({
                title: 'Error updating funnel',
                description: error.response?.data?.message || 'Something went wrong',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        }
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    // Median time-to-convert (ms) -> compact human string, Mixpanel-style ("2D", "5.8d", "3h").
    const formatDuration = (ms) => {
        if (ms == null || !isFinite(ms) || ms <= 0) return '—';
        const s = ms / 1000;
        if (s < 60) return `${Math.round(s)}s`;
        const m = s / 60;
        if (m < 60) return `${Math.round(m)}m`;
        const h = m / 60;
        if (h < 24) return `${h < 10 ? h.toFixed(1) : Math.round(h)}h`;
        const d = h / 24;
        return `${d < 10 ? d.toFixed(1) : Math.round(d)}d`;
    };

    const pct = (rate) => `${(Number(rate || 0) * 100).toFixed(1)}%`;

    // Mixpanel-style date-range presets -> conversion window (in days). Selecting a
    // preset drives the same window state the right-hand Metric panel edits.
    const DATE_PRESETS = [
        { label: 'Today',  long: 'Today',          unit: 'days', value: 1 },
        { label: '7D',     long: 'Last 7 days',    unit: 'days', value: 7 },
        { label: '30D',    long: 'Last 30 days',   unit: 'days', value: 30 },
        { label: '3M',     long: 'Last 3 months',  unit: 'days', value: 90 },
        { label: '6M',     long: 'Last 6 months',  unit: 'days', value: 180 },
        { label: '12M',    long: 'Last 12 months', unit: 'days', value: 365 },
    ];
    const applyPreset = (p) => { setWindowUnit(p.unit); setWindowValue(p.value); };
    const activePreset = DATE_PRESETS.find((p) => p.unit === windowUnit && p.value === windowValue);
    // Custom range = user picked "Custom…" OR the current window matches no preset.
    const showCustomRange = customRange || !activePreset;
    // Human label for the active breakdown ("currency × rail" for composite).
    const breakdownLabel = [selectedBreakdown, selectedBreakdown2].filter(Boolean).map((b) => b.replace('_', ' ')).join(' × ');

    // Headline number for the selected Measure, derived from the funnel totals.
    const headlineMetric = () => {
        if (!funnelData) return null;
        const d = funnelData[Object.keys(funnelData)[0]];
        const t = d?.totals;
        if (!t || !Array.isArray(t.step_counts)) return null;
        const finalCount = t.step_counts[t.step_counts.length - 1] || 0;
        if (measurement === 'total_conversions') return { value: formatNumber(finalCount), label: 'Total conversions' };
        if (measurement === 'time_to_convert') {
            const meds = (t.median_time_to_convert_ms || []).filter((x) => x != null);
            const totalMs = meds.reduce((a, b) => a + Number(b || 0), 0);
            return { value: totalMs ? formatDuration(totalMs) : '—', label: 'Median time to convert' };
        }
        if (measurement === 'value') {
            // FN08 — resolve the value source from the FIRST step that has one (matches
            // the preview payload). NEVER fall back to the conversion count under a
            // currency label, and never fabricate a "1 / converter" ratio.
            const valueVc = builderSteps
                .map((s) => conversionMap[s.eventName]?.value_config)
                .find((c) => c && (c.source_type === 'from_property' || c.source_type === 'fixed_value'));
            let total = t.value_sum;
            if (total == null && valueVc?.source_type === 'fixed_value') total = finalCount * (Number(valueVc.value) || 0);
            if (total == null) {
                // No real value aggregate → explicit empty state (no count, no ratio).
                return { value: '—', label: 'Total value', sub: 'No value source set' };
            }
            const perUser = t.value_per_user != null ? t.value_per_user : (finalCount ? total / finalCount : 0);
            return { value: formatNumber(Math.round(total)), label: 'Total value', sub: `${formatNumber(Math.round(perUser))} / converter` };
        }
        return { value: `${((t.total_conversion_rate || 0) * 100).toFixed(1)}%`, label: 'Overall conversion' };
    };

    const getBreakdownColors = (breakdown) => {
        const colorSchemes = {
            country: [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6', '#a855f7', '#10b981', '#f97316'],
            device: [PRIMARY_BG, '#ec4899', '#14b8a6'],
            utm_source: [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b'],
            browser: [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6'],
            os: [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b'],
            channel: [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6'],
        };
        return colorSchemes[breakdown] || [PRIMARY_BG, '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6'];
    };

    const getChartOptions = () => {
        if (!funnelData || Object.keys(funnelData).length === 0) return null;

        const firstBreakdown = Object.keys(funnelData)[0];
        const data = funnelData[firstBreakdown];

        if (!data || !data.steps) return null;

        const colors = getBreakdownColors(firstBreakdown);

        // Dynamic bar width based on number of steps
        const getBarWidth = (stepCount) => {
            if (stepCount <= 4) return 60;
            if (stepCount <= 6) return 50;
            if (stepCount <= 8) return 40;
            if (stepCount <= 12) return 30;
            return 25;
        };

        const barWidth = getBarWidth(builderSteps.length);

        if (!selectedBreakdown || firstBreakdown === 'default' || !(data.breakdown_rows && data.breakdown_rows.length)) {
            return {
                chart: {
                    type: 'column',
                    backgroundColor: 'transparent',
                    style: { fontFamily: "'Inter', sans-serif" },
                    height: 320
                },
                title: { text: null },
                xAxis: {
                    categories: data.steps.map((step, idx) => {
                        const stepName = builderSteps[idx]?.name || step.event;
                        return `${stepName}`;
                    }),
                    labels: {
                        rotation: 0,
                        align: 'center',
                        style: { fontSize: '11px' }
                    }
                },
                yAxis: {
                    title: { text: 'Users' },
                    labels: { style: { fontSize: '11px' } }
                },
                legend: { enabled: false },
                plotOptions: {
                    column: {
                        borderRadius: 4,
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            formatter: function () {
                                const p = ((this.point.pctFromFirst || 0) * 100).toFixed(1);
                                return `<div style="text-align:center;line-height:1.15"><div style="font-weight:700;font-size:12px;color:#1e293b">${p}%</div><div style="font-size:10px;color:#64748b">${formatNumber(this.y)}</div></div>`;
                            },
                            style: { textOutline: 'none' }
                        },
                        pointWidth: barWidth,
                        groupPadding: 0.1,
                        pointPadding: 0.05
                    }
                },
                series: [{
                    name: 'Users',
                    data: data.totals.step_counts.map((count, i) => ({
                        y: count,
                        color: PRIMARY_BG,
                        pctFromFirst: data.totals.conversion_from_first?.[i]
                            ?? (data.totals.step_counts[0] ? count / data.totals.step_counts[0] : 0)
                    }))
                }],
                credits: { enabled: false },
                tooltip: {
                    useHTML: true,
                    formatter: function () {
                        const p = ((this.point.pctFromFirst || 0) * 100).toFixed(1);
                        return `<b>${this.x}</b><br/>Contacts: ${formatNumber(this.y)}<br/>${p}% of entry`;
                    }
                }
            };
        } else {
            const series = data.breakdown_rows.map((row, index) => ({
                name: row.label,
                data: row.step_counts,
                color: colors[index % colors.length]
            }));

            return {
                chart: {
                    type: 'column',
                    backgroundColor: 'transparent',
                    style: { fontFamily: "'Inter', sans-serif" },
                    height: 320
                },
                title: { text: null },
                xAxis: {
                    categories: data.steps.map((step, idx) => {
                        const stepName = builderSteps[idx]?.name || step.event;
                        return `${stepName}`;
                    }),
                    labels: {
                        rotation: 0,
                        align: 'center',
                        style: { fontSize: '11px' }
                    }
                },
                yAxis: {
                    title: { text: 'Users' },
                    labels: { style: { fontSize: '11px' } }
                },
                legend: {
                    enabled: true,
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal',
                    maxHeight: 70,
                    itemStyle: { fontSize: '11px', fontWeight: '500' }
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        borderRadius: 4,
                        dataLabels: { enabled: false },
                        pointWidth: barWidth,
                        groupPadding: 0.1,
                        pointPadding: 0.05
                    }
                },
                series: series,
                credits: { enabled: false },
                tooltip: {
                    formatter: function () {
                        return `<b>${this.x}</b><br/>${this.series.name}: ${formatNumber(this.y)}`;
                    }
                }
            };
        }
    };

    const chartOptions = getChartOptions();

    if (loadingDetails || permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='xl' />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header />
            <BoxWrapper>
                <Flex justify="space-between" align="center" mb={6}>
                    <VStack align="flex-start" gap={2}>
                        <Button variant="outline" size="sm" leftIcon={<FiArrowLeft />} onClick={onBack} _hover={{ bg: "#0000001a !important", color: "black !important" }}>
                            Back to Funnels
                        </Button>
                        <Box>
                            <Heading size="lg" mb={1}>
                                {readOnly ? 'View Funnel' : (funnelId ? 'Edit Funnel' : 'Funnel Builder')}
                            </Heading>
                            <Text color="gray.600">
                                {readOnly ? 'Read-only funnel overview' : (funnelId ? 'Update funnel configuration' : 'Create and customize your conversion funnel')}
                            </Text>
                        </Box>
                    </VStack>
                    <Flex gap={2} align="center">
                        {effectiveReadOnly && (
                            <Tag size="sm" variant="outline" colorScheme="gray">View only</Tag>
                        )}
                        {readOnly ? (
                            canEditFunnel && (
                                <Button size="sm" bg={PRIMARY_BG} color={"white"} onClick={() => navigate(`/analytics/funnels-edit/${funnelId}`)}>
                                    Edit
                                </Button>
                            )
                        ) : funnelId ? (
                            canSave && (
                                <Button size="sm" bg={PRIMARY_BG} color={"white"} onClick={handleUpdateFunnel} isLoading={save}>
                                    Update Funnel
                                </Button>
                            )
                        ) : (
                            canSave && (
                                <>
                                    {/* BUG-FN_001: create directly as a draft, not only Active. */}
                                    <Button size="sm" variant="outline" onClick={() => handleSaveFunnel('draft')} isLoading={save}>
                                        Save as Draft
                                    </Button>
                                    <Button size="sm" bg={PRIMARY_BG} color={"white"} onClick={() => handleSaveFunnel('active')} isLoading={save}>
                                        Save Funnel
                                    </Button>
                                </>
                            )
                        )}
                    </Flex>
                </Flex>

                <Box className='box-style-card'>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <FormControl>
                            <FormLabel fontSize="sm">Funnel Name</FormLabel>
                            <Input
                                placeholder="Enter funnel name..."
                                value={funnelName}
                                onChange={(e) => setFunnelName(e.target.value)}
                                isReadOnly={effectiveReadOnly}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Description</FormLabel>
                            <Input
                                placeholder="Enter short description..."
                                value={funnelDescription}
                                onChange={(e) => setFunnelDescription(e.target.value)}
                                isReadOnly={effectiveReadOnly}
                            />
                        </FormControl>
                    </Grid>
                </Box>

                <Box mt={4}>
                    <Grid templateColumns="2fr 1fr" gap={6}>
                        {/* Left Column - Charts & Tables */}
                        <Box minW={0}>
                            <Box className='box-style-card' mb={6}>
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Heading size="sm">Funnel</Heading>
                                    {(() => {
                                        const h = headlineMetric();
                                        return h ? (
                                            <Box textAlign="right">
                                                <Text fontSize="xl" fontWeight="700" color={PRIMARY_BG} lineHeight="1.1">{h.value}</Text>
                                                <Text fontSize="xs" color="gray.500">{h.label}{h.sub ? ` · ${h.sub}` : ''}</Text>
                                            </Box>
                                        ) : null;
                                    })()}
                                </Flex>

                                {/* Breakdown legend renders BELOW the chart (Highcharts legend), not above. */}

                                <Box position="relative" minH="320px">
                                    {loading ? (
                                        <Center h="320px">
                                            <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='xl' />
                                        </Center>
                                    ) : chartOptions ? (
                                        <Box w="100%">
                                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                                        </Box>
                                    ) : (
                                        <Flex h="320px" align="center" justify="center" color="gray.500">
                                            <Box textAlign="center">
                                                <Box as={FiTarget} w={12} h={12} mx="auto" mb={4} opacity={0.5} />
                                                <Text>No steps added yet. Add steps to see the funnel visualization.</Text>
                                            </Box>
                                        </Flex>
                                    )}
                                </Box>
                            </Box>

                            {(() => {
                                const data = funnelData ? funnelData[Object.keys(funnelData)[0]] : null;
                                if (!data || !data.steps || !data.totals) return null;
                                const totals = data.totals;
                                const stepName = (i, step) => builderSteps[i]?.name || step.event;
                                // Mixpanel-style table: an Overall row (with median time-to-convert per
                                // step) followed by one row per breakdown segment when a breakdown is on.
                                const rows = [
                                    {
                                        key: '__overall__', label: 'Overall', isOverall: true,
                                        step_counts: totals.step_counts,
                                        conversion_from_prev: totals.conversion_from_prev,
                                        total_conversion_rate: totals.total_conversion_rate,
                                    },
                                    ...(selectedBreakdown ? (data.breakdown_rows || []) : []),
                                ];
                                return (
                                    <Box className='box-style-card' mb={6}>
                                        <Flex justify="space-between" align="center" mb={4}>
                                            <Heading size="sm" textTransform="capitalize">
                                                Conversion{breakdownLabel ? ` · by ${breakdownLabel}` : ''}
                                            </Heading>
                                            <Text fontSize="xs" color="gray.500">count · step-conv% · median time-to-convert</Text>
                                        </Flex>
                                        <Box position="relative" minH="120px">
                                            {loading ? (
                                                <Center h="120px">
                                                    <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='blue.500' size='xl' />
                                                </Center>
                                            ) : (
                                                <Box
                                                    w="100%"
                                                    overflowX="auto"
                                                    overflowY="visible"
                                                    css={{
                                                        '&::-webkit-scrollbar': { height: '8px' },
                                                        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                                                        '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
                                                        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                                                    }}
                                                >
                                                    <Table variant="simple" size="sm" style={{ minWidth: `${320 + (builderSteps.length * 160)}px` }}>
                                                        <Thead>
                                                            <Tr>
                                                                <Th position="sticky" left={0} bg={cardBg} zIndex={2} w="160px" boxShadow="2px 0 4px rgba(0,0,0,0.05)" textTransform="capitalize">
                                                                    {breakdownLabel || 'Segment'}
                                                                </Th>
                                                                <Th isNumeric w="110px">Total Conv.</Th>
                                                                {data.steps.map((step, index) => (
                                                                    <Th key={step.index} isNumeric w="160px">
                                                                        <Text isTruncated>{index + 1}. {stepName(index, step)}</Text>
                                                                    </Th>
                                                                ))}
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {rows.map((row) => {
                                                                const counts = row.step_counts || [];
                                                                const totalConv = row.total_conversion_rate != null
                                                                    ? row.total_conversion_rate
                                                                    : (counts[0] > 0 ? counts[counts.length - 1] / counts[0] : 0);
                                                                const rowBg = row.isOverall ? '#f8fafc' : cardBg;
                                                                return (
                                                                    <Tr key={row.key} fontWeight={row.isOverall ? 'semibold' : 'normal'}>
                                                                        <Td position="sticky" left={0} bg={rowBg} zIndex={1} boxShadow="2px 0 4px rgba(0,0,0,0.05)">
                                                                            <Flex align="center" gap={2}>
                                                                                {!row.isOverall && <Box w="10px" h="10px" borderRadius="sm" bg={PRIMARY_BG} flexShrink={0} />}
                                                                                <Text fontWeight="medium" isTruncated>{row.label}</Text>
                                                                            </Flex>
                                                                        </Td>
                                                                        <Td isNumeric bg={rowBg}>
                                                                            <Badge colorScheme={row.isOverall ? 'blue' : 'gray'} borderRadius="full" px={2}>{pct(totalConv)}</Badge>
                                                                        </Td>
                                                                        {counts.map((count, idx) => {
                                                                            const fromPrev = row.conversion_from_prev?.[idx]
                                                                                ?? (idx === 0 ? 1 : (counts[idx - 1] > 0 ? count / counts[idx - 1] : 0));
                                                                            const timeMs = row.isOverall ? totals.median_time_to_convert_ms?.[idx] : null;
                                                                            return (
                                                                                <Td key={idx} isNumeric bg={rowBg}>
                                                                                    <Text fontWeight="medium">{formatNumber(count)}</Text>
                                                                                    {idx > 0 && (
                                                                                        <Flex justify="flex-end" gap={1.5} fontSize="xs" color="gray.500">
                                                                                            <Text>{pct(fromPrev)}</Text>
                                                                                            {row.isOverall && timeMs != null && <Text>· {formatDuration(timeMs)}</Text>}
                                                                                        </Flex>
                                                                                    )}
                                                                                </Td>
                                                                            );
                                                                        })}
                                                                    </Tr>
                                                                );
                                                            })}
                                                        </Tbody>
                                                    </Table>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </Box>

                        {/* Right Column - Sidebar */}
                        <Box minW="300px">
                            <Box className='box-style-card' mb={6}>
                                <Box pb={3}>
                                    <Heading size="sm">Metric</Heading>
                                </Box>
                                <Box>
                                    <Box mb={4}>
                                        {builderSteps.map((step, index) => (
                                            <Flex key={step.id} align="center" gap={2} mb={2} fontSize="sm">
                                                <Flex align="center" justify="center" w={6} h={6} borderRadius="full" bg={PRIMARY_TINT} color={PRIMARY_BG} border="1px solid" borderColor={PRIMARY_BORDER} fontSize="xs" fontWeight="semibold" flexShrink={0}>
                                                    {index + 1}
                                                </Flex>
                                                <Text flex={1} isTruncated>{step.name}</Text>
                                                <Popover placement="left-start" isLazy onOpen={() => fetchStepAttributes(step.eventName)}>
                                                    <PopoverTrigger>
                                                        <IconButton size="xs" variant="ghost" icon={<FiFilter />}
                                                            color={(step.filters || []).length ? PRIMARY_BG : 'gray.400'}
                                                            aria-label="Filter step" />
                                                    </PopoverTrigger>
                                                    <PopoverContent w="340px" fontSize="sm" _focus={{ outline: 'none', boxShadow: 'md' }}>
                                                        <PopoverArrow />
                                                        <PopoverBody>
                                                            <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                                                                Filter · {step.name}
                                                            </Text>
                                                            {(step.filters || []).length === 0 && (
                                                                <Text fontSize="xs" color="gray.400" mb={2}>No filters — counts every {step.eventName}.</Text>
                                                            )}
                                                            {(step.filters || []).map((f, fi) => (
                                                                <Flex key={fi} align="center" gap={1} mb={2}>
                                                                    <Select size="xs" placeholder="property" flex={1} value={f.property} isDisabled={effectiveReadOnly}
                                                                        onFocus={() => fetchStepAttributes(step.eventName)}
                                                                        onChange={(e) => updateStepFilter(step.id, fi, { property: e.target.value })}>
                                                                        {f.property && !(stepAttributes[step.eventName] || []).find(a => a.name === f.property) && <option value={f.property}>{f.property}</option>}
                                                                        {(stepAttributes[step.eventName] || []).map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
                                                                    </Select>
                                                                    <Select size="xs" w="60px" value={f.operator} isDisabled={effectiveReadOnly} onChange={(e) => updateStepFilter(step.id, fi, { operator: e.target.value })}>
                                                                        <option value="eq">=</option>
                                                                        <option value="ne">≠</option>
                                                                        <option value="gt">&gt;</option>
                                                                        <option value="lt">&lt;</option>
                                                                        <option value="gte">≥</option>
                                                                        <option value="lte">≤</option>
                                                                        <option value="contains">has</option>
                                                                        <option value="is_set">set</option>
                                                                        <option value="is_not_set">unset</option>
                                                                    </Select>
                                                                    {f.operator !== 'is_set' && f.operator !== 'is_not_set' && (
                                                                        <Input size="xs" w="82px" placeholder="value" value={f.value} isReadOnly={effectiveReadOnly}
                                                                            onChange={(e) => updateStepFilter(step.id, fi, { value: e.target.value })} />
                                                                    )}
                                                                    {!effectiveReadOnly && (
                                                                        <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="Remove filter" onClick={() => removeStepFilter(step.id, fi)} />
                                                                    )}
                                                                </Flex>
                                                            ))}
                                                            {!effectiveReadOnly && (
                                                                <Button size="xs" variant="ghost" leftIcon={<FiPlus />} color={PRIMARY_BG} onClick={() => addStepFilter(step.id)}>Add filter</Button>
                                                            )}
                                                        </PopoverBody>
                                                    </PopoverContent>
                                                </Popover>
                                                {!effectiveReadOnly && (
                                                    <IconButton size="xs" variant="ghost" icon={<FiTrash2 />} aria-label="Remove step" onClick={() => handleRemoveStep(step.id)} />
                                                )}
                                            </Flex>
                                        ))}
                                        {!effectiveReadOnly && (
                                        <Menu onClose={() => setEventSearch('')}>
                                            <MenuButton _hover={{ bg: "#0000001a !important", color: "black !important" }} as={Button} size="sm" w="full" variant="outline" mt={2} bg={"white"} fontWeight="medium">
                                                <Flex align="center" justify="center" gap={2}>
                                                    <FiPlus size={14} />
                                                    <Text as="span" lineHeight="1">Add Step</Text>
                                                </Flex>
                                            </MenuButton>
                                            <MenuList maxH="340px" overflowY="auto" minW="260px" py={0}>
                                                <Box position="sticky" top={0} bg="white" zIndex={1} px={2} py={2} borderBottom="1px solid" borderColor="gray.100">
                                                    <Input
                                                        size="sm"
                                                        placeholder="Search events…"
                                                        value={eventSearch}
                                                        onChange={(e) => setEventSearch(e.target.value)}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                        autoFocus
                                                    />
                                                </Box>
                                                {(() => {
                                                    const q = eventSearch.trim().toLowerCase();
                                                    const list = q
                                                        ? availableEvents.filter((ev) => (ev.name || '').toLowerCase().includes(q) || (ev.display_name || '').toLowerCase().includes(q))
                                                        : availableEvents;
                                                    if (list.length === 0) {
                                                        return <Box px={3} py={3}><Text fontSize="sm" color="gray.500">No events match “{eventSearch}”.</Text></Box>;
                                                    }
                                                    return list.map((event) => (
                                                        <MenuItem key={event.name} onClick={() => handleAddStep(event)}>
                                                            <Box>
                                                                <Text fontWeight="medium">{event.display_name}</Text>
                                                                <Text fontSize="xs" color="gray.500">{event.name}</Text>
                                                            </Box>
                                                        </MenuItem>
                                                    ));
                                                })()}
                                            </MenuList>
                                        </Menu>
                                        )}
                                    </Box>

                                    <Box pt={4} borderTop="1px" borderColor={borderColor}>
                                        <Text color="gray.600" fontSize="sm" mb={2}>Measure</Text>
                                        <Select size="sm" value={measurement} isDisabled={effectiveReadOnly} onChange={(e) => setMeasurement(e.target.value)}>
                                            <option value="conversion_rate">Conversion Rate</option>
                                            <option value="total_conversions">Total Conversions</option>
                                            <option value="value">Value (sum)</option>
                                            <option value="time_to_convert">Time to Convert</option>
                                        </Select>
                                        {measurement === 'value' && (() => {
                                            // FN08 — reflect the ACTUAL configured value source (first step that
                                            // has one), not just the final step, so the hint never claims "no
                                            // value source" when one is configured on an earlier step.
                                            const srcStep = builderSteps.find((s) => {
                                                const c = conversionMap[s.eventName]?.value_config;
                                                return c && (c.source_type === 'from_property' || c.source_type === 'fixed_value');
                                            });
                                            const vc = srcStep ? conversionMap[srcStep.eventName]?.value_config : null;
                                            let hint;
                                            if (vc?.source_type === 'from_property' && vc.value) hint = `Summing "${vc.value}" on ${srcStep.name || srcStep.eventName}`;
                                            else if (vc?.source_type === 'fixed_value') hint = 'Fixed value × conversions';
                                            else hint = 'No value source set. Set one in Tools → Conversion Events.';
                                            return <Text fontSize="xs" color="gray.400" mt={1.5}>{hint}</Text>;
                                        })()}
                                    </Box>

                                    <Box pt={4} borderTop="1px" borderColor={borderColor}>
                                        <Text color="gray.600" fontSize="sm" mb={2}>Date range</Text>
                                        <Flex gap={1} bg="#f1f5f9" p={1} borderRadius="md" flexWrap="wrap" w="fit-content" maxW="100%">
                                            {DATE_PRESETS.map((p) => {
                                                const isActive = !showCustomRange && activePreset?.label === p.label;
                                                return (
                                                    <Button
                                                        key={p.label}
                                                        size="xs" variant="ghost" px={2.5} borderRadius="sm"
                                                        fontWeight={isActive ? '700' : '500'}
                                                        bg={isActive ? 'white' : 'transparent'}
                                                        color={isActive ? PRIMARY_BG : 'gray.600'}
                                                        boxShadow={isActive ? 'sm' : 'none'}
                                                        isDisabled={effectiveReadOnly}
                                                        _hover={{ bg: isActive ? 'white' : '#e2e8f0' }}
                                                        onClick={() => { applyPreset(p); setCustomRange(false); }}
                                                    >
                                                        {p.label}
                                                    </Button>
                                                );
                                            })}
                                            <Button
                                                size="xs" variant="ghost" px={2.5} borderRadius="sm"
                                                fontWeight={showCustomRange ? '700' : '500'}
                                                bg={showCustomRange ? 'white' : 'transparent'}
                                                color={showCustomRange ? PRIMARY_BG : 'gray.600'}
                                                boxShadow={showCustomRange ? 'sm' : 'none'}
                                                isDisabled={effectiveReadOnly}
                                                _hover={{ bg: showCustomRange ? 'white' : '#e2e8f0' }}
                                                onClick={() => setCustomRange(true)}
                                            >
                                                Custom
                                            </Button>
                                        </Flex>
                                        {showCustomRange && (
                                            <Flex align="center" gap={1} mt={2}>
                                                <Text fontSize="xs" color="gray.600">Last</Text>
                                                <Input size="xs" w="56px" type="number" min="1"
                                                    value={windowValue} isReadOnly={effectiveReadOnly}
                                                    onChange={(e) => setWindowValue(Math.max(1, parseInt(e.target.value, 10) || 1))} />
                                                <Select size="xs" w="82px" value={windowUnit} isDisabled={effectiveReadOnly} onChange={(e) => setWindowUnit(e.target.value)}>
                                                    <option value="hours">hours</option>
                                                    <option value="days">days</option>
                                                    <option value="weeks">weeks</option>
                                                </Select>
                                            </Flex>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box className='box-style-card' mb={6}>
                                <Box pb={3}>
                                    <Flex justify="space-between" align="center">
                                        <Heading size="sm">Breakdown</Heading>
                                        <Menu onOpen={() => builderSteps.forEach((s) => fetchStepAttributes(s.eventName))}>
                                            <MenuButton as={IconButton} size="xs" variant="ghost" icon={<FiPlus />} aria-label="Add breakdown"
                                                isDisabled={effectiveReadOnly || (!!selectedBreakdown && !!selectedBreakdown2) || builderSteps.length === 0} />
                                            <MenuList maxH="360px" overflowY="auto" minW="240px">
                                                {builderSteps.length === 0 && (
                                                    <MenuItem isDisabled fontSize="sm" color="gray.400">Add steps first</MenuItem>
                                                )}
                                                {builderSteps.map((step) => {
                                                    const attrs = stepAttributes[step.eventName];
                                                    return (
                                                        <MenuGroup key={step.id} title={step.name || step.eventName}>
                                                            {attrs === undefined && (
                                                                <MenuItem isDisabled pl={6} fontSize="sm" color="gray.400">Loading…</MenuItem>
                                                            )}
                                                            {attrs && attrs.length === 0 && (
                                                                <MenuItem isDisabled pl={6} fontSize="sm" color="gray.400">No properties</MenuItem>
                                                            )}
                                                            {(attrs || []).map((a) => (
                                                                <MenuItem
                                                                    key={`${step.id}:${a.name}`}
                                                                    pl={6}
                                                                    onClick={() => handleSelectBreakdown(a.name)}
                                                                    isDisabled={selectedBreakdown === a.name || selectedBreakdown2 === a.name}
                                                                >
                                                                    {a.name}{a.type && a.type !== 'string' ? ` (${a.type})` : ''}
                                                                </MenuItem>
                                                            ))}
                                                        </MenuGroup>
                                                    );
                                                })}
                                            </MenuList>
                                        </Menu>
                                    </Flex>
                                </Box>
                                <Box>
                                    {selectedBreakdown ? (
                                        <Flex direction="column" gap={2}>
                                            <Box p={3} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                                                <Flex justify="space-between" align="center">
                                                    <Flex align="center" gap={2}>
                                                        <Box as={FiTarget} color="cyan.500" />
                                                        <Text fontSize="sm" fontWeight="medium">
                                                            {breakdownOptions.find(opt => opt.value === selectedBreakdown)?.label || selectedBreakdown}
                                                        </Text>
                                                    </Flex>
                                                    {!effectiveReadOnly && (
                                                        <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="Remove breakdown" onClick={handleRemoveBreakdown} />
                                                    )}
                                                </Flex>
                                            </Box>
                                            {selectedBreakdown2 ? (
                                                <Box p={3} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                                                    <Flex justify="space-between" align="center">
                                                        <Flex align="center" gap={2}>
                                                            <Box as={FiTarget} color="purple.500" />
                                                            <Text fontSize="sm" fontWeight="medium">
                                                                {breakdownOptions.find(opt => opt.value === selectedBreakdown2)?.label || selectedBreakdown2}
                                                            </Text>
                                                        </Flex>
                                                        {!effectiveReadOnly && (
                                                            <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="Remove second breakdown" onClick={handleRemoveBreakdown2} />
                                                        )}
                                                    </Flex>
                                                </Box>
                                            ) : (
                                                <Text fontSize="xs" color="gray.400" textAlign="center">
                                                    Add a second dimension to split further (e.g. Currency × Rail).
                                                </Text>
                                            )}
                                        </Flex>
                                    ) : (
                                        <Text fontSize="xs" color="gray.500" textAlign="center">
                                            Add a breakdown to segment your funnel data.
                                        </Text>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Box>
            </BoxWrapper>
        </>
    );
};

export default FunnelBuilder;